/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CollapsibleCodeBlock } from './collapsible-code-block';
import debugMessages from './messages';
import type { InsightGroupDebugInfo } from './types';

const styles = cssMap({
	container: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		marginBlockStart: token('space.100'),
	},
	metricLabel: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	metricValue: {
		font: token('font.code'),
		color: token('color.text.subtle'),
	},
	successValue: {
		font: token('font.code'),
		color: token('color.text.success'),
	},
	warningValue: {
		font: token('font.code'),
		color: token('color.text.warning'),
	},
	dangerValue: {
		font: token('font.code'),
		color: token('color.text.danger'),
	},
	errorPanel: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: token('color.background.danger'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
		color: token('color.text.danger'),
	},
});

function formatDuration(secs: number): string {
	if (secs < 60) {
		return `${secs}s`;
	}
	const mins = Math.floor(secs / 60);
	const remainSecs = secs % 60;
	return `${mins}m ${remainSecs}s`;
}

interface DebugGroupPanelProps {
	debugInfo: InsightGroupDebugInfo;
}

/**
 * Group-level debug panel showing generation metrics, prompt, and LLM response.
 */
export const DebugGroupPanel = ({ debugInfo }: DebugGroupPanelProps): JSX.Element => {
	const { generatedAt, prompt, llmResponse, error, durationSecs, attempts } = debugInfo;
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.100" xcss={styles.container}>
			{/* Metrics bar */}
			<Flex gap="space.150" alignItems="center" wrap="wrap">
				<Flex gap="space.050" alignItems="center">
					<Box as="span" xcss={styles.metricLabel}>
						{formatMessage(debugMessages.durationLabel)}
					</Box>
					<Box
						as="span"
						xcss={
							durationSecs >= 600
								? styles.dangerValue
								: durationSecs >= 300
									? styles.warningValue
									: styles.successValue
						}
					>
						{formatDuration(durationSecs)}
					</Box>
				</Flex>
				<Flex gap="space.050" alignItems="center">
					<Box as="span" xcss={styles.metricLabel}>
						{formatMessage(debugMessages.attemptsLabel)}
					</Box>
					<Box as="span" xcss={attempts > 1 ? styles.warningValue : styles.metricValue}>
						{attempts}
					</Box>
				</Flex>
				{error && (
					<Flex gap="space.050" alignItems="center">
						<Box as="span" xcss={styles.metricLabel}>
							{formatMessage(debugMessages.errorLabel)}
						</Box>
						<Box as="span" xcss={styles.dangerValue}>
							{formatMessage(debugMessages.errorYes)}
						</Box>
					</Flex>
				)}
				<Box as="span" xcss={styles.metricLabel}>
					{formatMessage(debugMessages.generatedLabel, {
						timestamp: new Date(generatedAt).toLocaleString(),
					})}
				</Box>
			</Flex>

			{/* Error panel */}
			{error && <Box xcss={styles.errorPanel}>{error}</Box>}

			{/* Prompt viewer */}
			<CollapsibleCodeBlock title={formatMessage(debugMessages.promptTitle)} content={prompt} />

			{/* LLM Response viewer */}
			<CollapsibleCodeBlock
				title={formatMessage(debugMessages.llmResponseTitle)}
				content={llmResponse}
				isJson
			/>
		</Stack>
	);
};
